import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Banner, Button, OrderButtons, SectionLabel } from '../../../design/components'
import { moved } from '../../../lib/order'
import { jobStatusLine } from '../../jobs/types'
import { useGenerationJob } from '../../jobs/useGenerationJob'
import { generatePageImage } from '../mutations'
import { comicKeys } from '../queries'
import { pageTypeLabel, type ComicPage, type ComicScene, type PageType } from '../types'
import type { ComicEditing } from '../useComicEditing'
import { PagePromptComposer } from './PagePromptComposer'
import styles from './comics.module.css'

/**
 * One page of the comic: what it is, what happens on it, and the art.
 *
 * The page is the unit everything in this domain hangs off — the script, the uploaded or
 * generated images, and the ordering — so it owns all three rather than having them
 * scattered across the scene card above it.
 *
 * **Generation is per page and its progress is inline**, for the same reason the scene
 * tracker's pages are: a job belongs to one page, and a shared progress line on the card
 * would not say which. What a finished job returns is a *pointer* to an image already
 * saved and linked, so settling refetches the comic rather than reading a payload.
 */
export function ComicPageRow({
  page,
  index,
  scene,
  editing,
  /** Every image in the comic, offered as generation references. See the composer. */
  references,
}: {
  page: ComicPage
  index: number
  scene: ComicScene
  editing: ComicEditing
  references: ReferenceImage[]
}) {
  const queryClient = useQueryClient()
  const [editingScript, setEditingScript] = useState(false)
  const [composing, setComposing] = useState(false)

  // The composed prompt, held in a ref because `start` is called by the job hook after the
  // composer has closed over it — reading it from state there would capture the value as
  // of the render that created the hook, which is the empty one.
  const lastRequest = useRef<{ blocks: PromptBlock[]; title?: string }>({ blocks: [] })

  const job = useGenerationJob({
    start: () => generatePageImage(editing.comicId, page.id, lastRequest.current),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: comicKeys.sheet(editing.comicId) })
      setComposing(false)
    },
  })

  const isCover = page.type !== 'Page'
  const pageIds = scene.pages.map((entry) => entry.id)

  return (
    <div className={`${styles.page} ${isCover ? styles.pageCover : ''}`}>
      <div className={styles.rowHead}>
        <span className={styles.rowName}>
          {page.type === 'Page' ? `Page ${index + 1}` : pageTypeLabel(page.type)}
        </span>

        {editing.canEdit && (
          <span className={styles.orderButtons}>
            <OrderButtons
              label={`page ${index + 1}`}
              index={index}
              total={scene.pages.length}
              disabled={editing.reorderPages.isPending}
              onMove={(to) =>
                editing.reorderPages.mutate({
                  comicSceneId: scene.id,
                  ids: moved(pageIds, index, to),
                })
              }
            />
            <Button
              variant="danger"
              disabled={editing.removePage.isPending}
              onClick={() => editing.removePage.mutate({ pageId: page.id })}
            >
              Remove
            </Button>
          </span>
        )}
      </div>

      {page.images.length > 0 && (
        <div className={styles.pageImages}>
          {page.images.map((image) => (
            <div key={image.id} className={styles.imageTile}>
              <img
                className={styles.pageImage}
                src={image.urlSmall ?? image.urlThumb ?? image.url ?? ''}
                alt={image.title ?? `${pageTypeLabel(page.type)} art`}
                loading="lazy"
              />
              {editing.canEdit && (
                <Button
                  variant="secondary"
                  disabled={editing.removeImage.isPending}
                  onClick={() => editing.removeImage.mutate({ imageId: image.id ?? 0 })}
                >
                  Delete
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {editingScript ? (
        <ScriptForm page={page} editing={editing} onClose={() => setEditingScript(false)} />
      ) : (
        <>
          {page.script === '' ? (
            <p className={styles.empty}>No script written.</p>
          ) : (
            <>
              <SectionLabel tone="muted" micro>
                Script
              </SectionLabel>
              <p className={styles.rowText}>{page.script}</p>
            </>
          )}

          {editing.canEdit && (
            <div className={styles.rowActions}>
              <Button variant="secondary" onClick={() => setEditingScript(true)}>
                {page.script === '' ? 'Write the script' : 'Edit the script'}
              </Button>
              <UploadButton page={page} editing={editing} />
              {!composing && !job.isRunning && (
                <Button variant="secondary" onClick={() => setComposing(true)}>
                  {page.images.length === 0 ? 'Generate the art' : 'Generate another'}
                </Button>
              )}
            </div>
          )}
        </>
      )}

      {editing.canEdit && composing && !job.isRunning && (
        <PagePromptComposer
          page={page}
          references={references}
          onCancel={() => setComposing(false)}
          onGenerate={(request) => {
            lastRequest.current = request
            job.run()
          }}
        />
      )}

      {job.isRunning && (
        <div className={styles.rowActions}>
          <span className={styles.rowText} role="status">
            {jobStatusLine(job.job, job.assumedFinished)}
          </span>
          <Button variant="secondary" onClick={job.cancel}>
            Cancel
          </Button>
        </div>
      )}

      {job.failure !== null && <Banner tone="error">{job.failure}</Banner>}
    </div>
  )
}

/** A prompt block, exactly as the API takes it. See `PagePromptComposer`. */
export interface PromptBlock {
  kind: 'Text' | 'Image'
  order: number
  text?: string
  comicImageId?: number
}

/** An image this comic already holds, offered as a reference to draw the next one from. */
export interface ReferenceImage {
  id: number
  url: string
  label: string
}

function ScriptForm({
  page,
  editing,
  onClose,
}: {
  page: ComicPage
  editing: ComicEditing
  onClose: () => void
}) {
  const [script, setScript] = useState(page.script)
  const [type, setType] = useState<PageType>(page.type)

  return (
    <form
      className={styles.inlineForm}
      onSubmit={(event) => {
        event.preventDefault()
        editing.updatePage.mutate(
          { pageId: page.id, body: { type, script: script === '' ? null : script } },
          { onSuccess: onClose },
        )
      }}
    >
      <label className={styles.field}>
        <SectionLabel tone="muted" micro>
          Script
        </SectionLabel>
        <textarea
          className={styles.textarea}
          value={script}
          autoFocus
          maxLength={4000}
          onChange={(event) => setScript(event.target.value)}
        />
      </label>

      {/* Changing a page into a cover can be refused — a comic holds one of each across
          all its scenes — and the refusal arrives as a 409 with the reason, which the
          card above renders. The option is offered because the flags describe what the
          comic has, not what this page may become. */}
      <label className={styles.field}>
        <SectionLabel tone="muted" micro>
          Kind
        </SectionLabel>
        <select
          className={styles.select}
          value={type}
          onChange={(event) => setType(event.target.value as PageType)}
        >
          <option value="FrontCover">Front cover</option>
          <option value="Page">Page</option>
          <option value="BackCover">Back cover</option>
        </select>
      </label>

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={editing.updatePage.isPending}>
          {editing.updatePage.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  )
}

/**
 * Uploading art onto a page.
 *
 * A hidden file input behind a button rather than a form: a page takes several images and
 * needs no title, so there is nothing to fill in — choosing the file *is* the submit.
 * JPEG/PNG and the 5 MB ceiling are the server's, and repeating them is the useful thing
 * to say when it refuses.
 */
function UploadButton({ page, editing }: { page: ComicPage; editing: ComicEditing }) {
  const input = useRef<HTMLInputElement>(null)

  return (
    <>
      <input
        ref={input}
        className={styles.file}
        type="file"
        accept="image/jpeg,image/png"
        hidden
        aria-label={`Upload art for ${pageTypeLabel(page.type).toLowerCase()}`}
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file === undefined) return

          editing.uploadImage.mutate({ pageId: page.id, file })
          // Cleared so choosing the same file twice fires a second change event.
          event.target.value = ''
        }}
      />
      <Button
        variant="secondary"
        disabled={editing.uploadImage.isPending}
        onClick={() => input.current?.click()}
      >
        {editing.uploadImage.isPending ? 'Uploading…' : 'Upload art'}
      </Button>
    </>
  )
}
